import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { stockApi, stockBajoApi, descargarArchivoAutenticado, etiquetasCodigoBarrasApi, descargarEtiquetasPost } from '../lib/api';
import { normalizarCodigo } from '../lib/codigoBarras';
import { Edit, Save, X, Search, Package, Plus, Upload, Eye, Download, CheckSquare, Square, Filter, Trash2, RotateCcw, Wrench, ScanLine, AlertTriangle, Barcode } from 'lucide-react';
import { toast } from 'sonner';
import { fechaLima } from '../lib/fechas';
import KitEditor, { EstadoKit, estadoKitDe, firmaKit } from '../components/KitEditor';

// Lista fija para el selector de Unidad: evita errores de tipeo (antes era texto
// libre). El stock y los precios de un producto "metro" siempre se guardan en
// metros; "cm" es para productos que se miden/venden directamente en centimetros.
const UNIDADES_DISPONIBLES = ['unidad', 'metro', 'cm', 'litro', 'galón', 'kg', 'par', 'juego'];

const StockCompleto = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [productos, setProductos] = useState<any[]>([]);
  const [editandoProducto, setEditandoProducto] = useState<any>(null);
  // Kit (2026-11-02): estado del editor de kit del producto en edición y su firma original
  const [kitEdit, setKitEdit] = useState<EstadoKit | null>(null);
  const [kitFirmaOriginal, setKitFirmaOriginal] = useState('');
  // Para productos "metro": permite tipear el stock en centimetros en vez de metros.
  // El valor real (editandoProducto.stock / nuevoProducto.stock) siempre queda en
  // metros, esto solo cambia como se muestra/interpreta el numero en el input.
  const [editandoStockEnCm, setEditandoStockEnCm] = useState(false);
  const [nuevoStockEnCm, setNuevoStockEnCm] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [productoDetalles, setProductoDetalles] = useState<any>(null);
  const [mostrarModalExportar, setMostrarModalExportar] = useState(false);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [filtroExportar, setFiltroExportar] = useState<'todos' | 'con_stock' | 'sin_stock'>('todos');
  const [vistaEliminados, setVistaEliminados] = useState(false);
  const [productosEliminados, setProductosEliminados] = useState<any[]>([]);
  const [mostrarStockBajo, setMostrarStockBajo] = useState(false);
  const [stockBajoLista, setStockBajoLista] = useState<any[]>([]);
  const [cargandoStockBajo, setCargandoStockBajo] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [mostrarEtiquetas, setMostrarEtiquetas] = useState(false);
  const [etiquetasLista, setEtiquetasLista] = useState<any[]>([]);
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState<Set<string>>(new Set());
  const [cargandoEtiquetas, setCargandoEtiquetas] = useState(false);
  const [imprimiendoEtiquetas, setImprimiendoEtiquetas] = useState(false);
  // Derivado directo de la URL en cada render: Stock y Herramientas son catalogos
  // separados sin mezcla, "/stock" es solo para venta, "/stock?tipo=herramienta" es solo herramientas.
  const esVistaHerramientas = searchParams.get('tipo') === 'herramienta';
  const [nuevoProducto, setNuevoProducto] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    precio_venta: 0,
    precio_compra: 0,
    categoria: '',
    stock_minimo: 0,
    stock: 0,
    unidad: 'unidad'
  });
  const [codigoEscaneado, setCodigoEscaneado] = useState('');

  const cargarProductos = async () => {
    try {
      const data = await stockApi.getFull();
      setProductos(data || []);
    } catch (error) {
      console.error('Error cargando productos:', error);
    }
  };

  useEffect(() => {
    cargarProductos();
    // Sincronización periódica (ya no hay suscripción realtime de Supabase)
    const intervalo = setInterval(cargarProductos, 30000);
    return () => clearInterval(intervalo);
  }, []);

  const iniciarEdicion = (producto: any) => {
    setEditandoProducto({ ...producto });
    setEditandoStockEnCm(false);
    const k = estadoKitDe(producto);
    setKitEdit(k);
    setKitFirmaOriginal(firmaKit(k));
  };

  // El lector de codigo de barras funciona como teclado: escribe el codigo y
  // presiona Enter solo. Si el producto ya existe lo abre para editar, si no,
  // abre el formulario de nuevo producto con ese codigo ya puesto.
  const manejarEscaneo = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const codigo = codigoEscaneado.trim();
    if (!codigo) return;
    const encontrado = productos.find(p => normalizarCodigo(p.codigo) === normalizarCodigo(codigo));
    if (encontrado) {
      iniciarEdicion(encontrado);
      toast.success(`Producto encontrado: ${encontrado.nombre}`);
    } else {
      setNuevoProducto({
        codigo,
        nombre: '',
        descripcion: '',
        precio_venta: 0,
        precio_compra: 0,
        categoria: '',
        stock_minimo: 0,
        stock: 0,
        unidad: 'unidad'
      });
      setMostrarFormulario(true);
      toast.info('Código nuevo, completa los datos del producto');
    }
    setCodigoEscaneado('');
  };

  // Busca si el codigo ya lo tiene otro producto (excluyendo el que se esta editando,
  // si corresponde). Codigos vacios no cuentan como duplicado: se autogeneran al guardar.
  const buscarDuenoDelCodigo = (codigo: string, idExcluir?: string) => {
    const codigoNormalizado = (codigo || '').trim().toLowerCase();
    if (!codigoNormalizado) return null;
    return productos.find(p =>
      p.id !== idExcluir && (p.codigo || '').trim().toLowerCase() === codigoNormalizado
    ) || null;
  };

  const guardarEdicion = async () => {
    if (!editandoProducto.nombre.trim()) {
      alert('El nombre no puede estar vacío');
      return;
    }

    const duenoCodigo = buscarDuenoDelCodigo(editandoProducto.codigo, editandoProducto.id);
    if (duenoCodigo) {
      alert(`Ese código ya lo tiene "${duenoCodigo.nombre}". Usa uno distinto.`);
      return;
    }
    
    if (editandoProducto.precio_venta < 0) {
      alert('El precio de venta no puede ser negativo');
      return;
    }
    
    if (editandoProducto.precio_compra < 0) {
      alert('El precio de compra no puede ser negativo');
      return;
    }
    
    if ((editandoProducto.stock || 0) < 0) {
      alert('La cantidad en stock no puede ser negativa');
      return;
    }
    
    if ((editandoProducto.stock_minimo || 0) < 0) {
      alert('El stock mínimo no puede ser negativo');
      return;
    }
    
    // Kit: validar antes de guardar nada
    const kitCambio = !!kitEdit && editandoProducto.tipo !== 'herramienta' && firmaKit(kitEdit) !== kitFirmaOriginal;
    if (kitCambio && kitEdit!.es_kit) {
      if (!kitEdit!.componentes.length) { alert('Agrega al menos una pieza al kit'); return; }
      if (kitEdit!.componentes.some(c => !(c.cantidad > 0))) { alert('Cada pieza del kit debe tener una cantidad mayor a 0'); return; }
    }

    try {
      await stockApi.update(editandoProducto.id, {
        codigo: editandoProducto.codigo || '',
        nombre: editandoProducto.nombre,
        descripcion: editandoProducto.descripcion || '',
        precio_venta: editandoProducto.precio_venta,
        precio_compra: editandoProducto.precio_compra,
        categoria: editandoProducto.categoria || '',
        stock_minimo: editandoProducto.stock_minimo || 0,
        stock: editandoProducto.stock || 0,
        unidad: editandoProducto.unidad || 'unidad',
        tipo: editandoProducto.tipo || 'venta',
        condicion: editandoProducto.tipo === 'herramienta' ? (editandoProducto.condicion || 'en_uso') : 'en_uso'
      });
      if (kitCambio) {
        const r = await stockApi.guardarKit(editandoProducto.id, {
          es_kit: kitEdit!.es_kit,
          componentes: kitEdit!.componentes.map(c => ({ producto_id: c.producto_id, cantidad: c.cantidad })),
          sumar_stock_actual: kitEdit!.sumar_stock_actual,
        });
        if (!kitEdit!.es_kit) toast.success('Ya no es kit: ahora se vende como pieza única');
        else toast.success(`Kit guardado. Kits armables: ${r.disponibles}` +
          (r.stock_pasado_a_piezas ? ` (las unidades que había se pasaron a sus piezas)` : ''));
      }
      setEditandoProducto(null);
      setKitEdit(null);
      await cargarProductos();
    } catch (error: any) {
      alert('Error al guardar: ' + error.message);
    }
  };

  const cancelarEdicion = () => {
    setEditandoProducto(null);
    setKitEdit(null);
  };

  const mostrarDetallesProducto = (producto: any) => {
    setProductoDetalles(producto);
    setMostrarDetalles(true);
  };

  const cerrarDetalles = () => {
    setMostrarDetalles(false);
    setProductoDetalles(null);
  };

  const cargarProductosEliminados = async () => {
    try {
      const data = await stockApi.getFull({ eliminado: true });
      const filtrados = (data || []).filter((p: any) => (p.tipo || 'venta') === (esVistaHerramientas ? 'herramienta' : 'venta'));
      setProductosEliminados(filtrados);
    } catch (error) {
      console.error('Error cargando productos eliminados:', error);
    }
  };

  const eliminarProducto = async (id: string, nombre: string) => {
    if (!confirm(`¿Eliminar el producto "${nombre}"? Podras verlo en la seccion de Eliminados.`)) return;
    try {
      await stockApi.update(id, { eliminado: true, eliminado_at: new Date().toISOString() });
      await cargarProductos();
      cargarProductosEliminados();
    } catch (error: any) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  const restaurarProducto = async (id: string, nombre: string) => {
    if (!confirm(`¿Restaurar el producto "${nombre}"?`)) return;
    try {
      await stockApi.update(id, { eliminado: false, eliminado_at: null });
      await cargarProductos();
      cargarProductosEliminados();
    } catch (error: any) {
      alert('Error al restaurar: ' + error.message);
    }
  };

  const agregarProducto = async () => {
    if (!nuevoProducto.nombre.trim()) {
      alert(`El nombre ${esVistaHerramientas ? 'de la herramienta' : 'del producto'} es obligatorio`);
      return;
    }

    const duenoCodigo = buscarDuenoDelCodigo(nuevoProducto.codigo);
    if (duenoCodigo) {
      alert(`Ese código ya lo tiene "${duenoCodigo.nombre}". Usa uno distinto.`);
      return;
    }

    const { nombre, descripcion, precio_venta, precio_compra, categoria, stock_minimo, stock, codigo, unidad } = nuevoProducto;

    const productoParaInsertar = {
      codigo: codigo || undefined,
      nombre,
      descripcion,
      precio_venta: parseFloat(precio_venta.toString()) || 0,
      precio_compra: parseFloat(precio_compra.toString()) || 0,
      categoria,
      stock_minimo: parseFloat(stock_minimo.toString()) || 0,
      stock: parseFloat(stock.toString()) || 0,
      unidad: unidad || 'unidad',
      // El tipo lo decide la vista donde estas parado, no un selector: evita que se
      // cuelen herramientas en Stock o productos de venta en Herramientas.
      tipo: esVistaHerramientas ? 'herramienta' : 'venta'
    };

    try {
      await stockApi.create(productoParaInsertar);
      setNuevoProducto({
        codigo: '',
        nombre: '',
        descripcion: '',
        precio_venta: 0,
        precio_compra: 0,
        categoria: '',
        stock_minimo: 0,
        stock: 0,
        unidad: 'unidad'
      });
      setNuevoStockEnCm(false);
      setMostrarFormulario(false);
      await cargarProductos();
      alert(esVistaHerramientas ? 'Herramienta agregada exitosamente' : 'Producto agregado exitosamente');
    } catch (error: any) {
      alert(`Error al agregar ${esVistaHerramientas ? 'la herramienta' : 'producto'}: ` + error.message);
    }
  };

  const productosFiltrados = productos
    .filter(producto => {
      const texto = busqueda.trim().toLowerCase();
      if (!texto) return true;
      const nombreMatch = producto.nombre.toLowerCase().includes(texto);
      const codigoMatch = (producto.codigo || '').toLowerCase().includes(texto);
      // Tolera que el escaneo/tipeo haya puesto apostrofe en vez de guion (ver
      // lib/codigoBarras.ts): compara tambien las versiones sin esos separadores.
      const textoNormalizado = normalizarCodigo(texto);
      const codigoNormalizadoMatch = textoNormalizado.length > 0 && normalizarCodigo(producto.codigo).includes(textoNormalizado);
      return nombreMatch || codigoMatch || codigoNormalizadoMatch;
    })
    .filter(producto => (producto.tipo || 'venta') === (esVistaHerramientas ? 'herramienta' : 'venta'));


  const abrirModalExportar = () => {
    // Solo lo que se ve en la vista actual (Stock=venta o Herramientas), nunca mezclado.
    const todos = [...productosFiltrados]
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));
    setSeleccionados(new Set(todos.map((p: any) => p.id)));
    setFiltroExportar('todos');
    setMostrarModalExportar(true);
  };

  const descargarSeleccionados = () => {
    const ahora = new Date();
    const fecha = ahora.toLocaleDateString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric" });
    const hora  = ahora.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
    const nombreArchivo = `Stock_Seleccionados_${fechaLima(ahora)}.xlsx`;

    const base = [...productosFiltrados]
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" }));
    const seleccionadosList = base.filter((p: any) => seleccionados.has(p.id));
    const totalProductos = seleccionadosList.length;
    const productosStockBajo = seleccionadosList.filter((p: any) => (p.stock || 0) <= (p.stock_minimo || 0)).length;
    const valorTotalStock = seleccionadosList.reduce((acc: number, p: any) => acc + (p.stock || 0) * (p.precio_venta || 0), 0);

    const filas: any[][] = [];
    filas.push(["SISTEMA RÁPIDO - GESTIÓN DE REPUESTOS Y SERVICIOS"]);
    filas.push(["REPORTE DE STOCK SELECCIONADOS"]);
    filas.push([`Fecha de emisión: ${fecha}  |  Hora: ${hora}`]);
    filas.push([`Total seleccionados: ${totalProductos}  |  Stock bajo: ${productosStockBajo}  |  Valor total: S/ ${valorTotalStock.toFixed(2)}`]);
    filas.push([]);
    filas.push(["N°", "CÓDIGO", "PRODUCTO / DESCRIPCIÓN", "CATEGORÍA", "UNIDAD", "STOCK ACTUAL", "STOCK MÍNIMO", "ESTADO", "PRECIO COMPRA (S/)", "PRECIO VENTA (S/)", "VALOR EN STOCK (S/)"]);

    seleccionadosList.forEach((p: any, i: number) => {
      const stockActual  = p.stock || 0;
      const stockMinimo  = p.stock_minimo || 0;
      const precioVenta  = p.precio_venta || 0;
      const precioCompra = p.precio_compra || 0;
      const estado       = stockActual === 0 ? "SIN STOCK" : stockActual <= stockMinimo ? "STOCK BAJO" : "NORMAL";
      filas.push([i + 1, p.codigo || "-", p.nombre || "", p.categoria || "-", p.unidad || "unidad", stockActual, stockMinimo, estado, precioCompra, precioVenta, stockActual * precioVenta]);
    });

    filas.push([]);
    filas.push(["TOTALES", "", "", `${totalProductos} productos`, "", seleccionadosList.reduce((a: number, p: any) => a + (p.stock || 0), 0), "", `${productosStockBajo} en stock bajo`, "", "", valorTotalStock]);

    const ws = XLSX.utils.aoa_to_sheet(filas);
    ws["!cols"] = [
      { wch: 5 }, { wch: 20 }, { wch: 42 }, { wch: 15 }, { wch: 10 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");
    XLSX.writeFile(wb, nombreArchivo);
    setMostrarModalExportar(false);
  };


  const abrirStockBajo = async () => {
    setMostrarStockBajo(true);
    setCargandoStockBajo(true);
    try {
      const data = await stockBajoApi.getAll();
      setStockBajoLista(data || []);
    } catch (error: any) {
      toast.error('Error al cargar stock bajo: ' + error.message);
    } finally {
      setCargandoStockBajo(false);
    }
  };

  const descargarPdfStockBajo = async () => {
    setDescargando(true);
    try {
      await descargarArchivoAutenticado('/api/stock/bajo/pdf', `stock_bajo_${fechaLima(new Date())}.pdf`);
    } catch (error: any) {
      toast.error('Error al generar el PDF: ' + error.message);
    } finally {
      setDescargando(false);
    }
  };

  const abrirModalEtiquetas = async () => {
    setMostrarEtiquetas(true);
    setCargandoEtiquetas(true);
    try {
      const data = await etiquetasCodigoBarrasApi.getLista();
      setEtiquetasLista(data || []);
      // Por defecto se preseleccionan los que realmente necesitan etiqueta nueva:
      // nunca impresos o editados despues de su ultima impresion. Los que ya estan
      // al dia quedan sin marcar para no reimprimirlos de arranque.
      const porDefecto = new Set(
        (data || []).filter((p: any) => p.estado_etiqueta !== 'al_dia').map((p: any) => p.id)
      );
      setEtiquetasSeleccionadas(porDefecto);
    } catch (error: any) {
      toast.error('Error al cargar productos para etiquetar: ' + error.message);
    } finally {
      setCargandoEtiquetas(false);
    }
  };

  const alternarSeleccionEtiqueta = (id: string) => {
    setEtiquetasSeleccionadas(prev => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
      return nuevo;
    });
  };

  const imprimirEtiquetasSeleccionadas = async () => {
    if (etiquetasSeleccionadas.size === 0) {
      toast.error('Seleccioná al menos un producto');
      return;
    }
    setImprimiendoEtiquetas(true);
    try {
      await descargarEtiquetasPost(
        { modo: 'seleccionados', ids: Array.from(etiquetasSeleccionadas) },
        `etiquetas_codigo_barras_${fechaLima(new Date())}.pdf`
      );
      toast.success(`${etiquetasSeleccionadas.size} etiqueta(s) generada(s)`);
      await abrirModalEtiquetas();
    } catch (error: any) {
      toast.error('Error al generar las etiquetas: ' + error.message);
    } finally {
      setImprimiendoEtiquetas(false);
    }
  };

  const imprimirTodasLasEtiquetas = async () => {
    if (!confirm(`Esto reimprime TODOS los ${etiquetasLista.length} productos con código interno, incluso los que ya estaban al día. ¿Continuar?`)) return;
    setImprimiendoEtiquetas(true);
    try {
      await descargarEtiquetasPost(
        { modo: 'todos' },
        `etiquetas_codigo_barras_todos_${fechaLima(new Date())}.pdf`
      );
      toast.success('Etiquetas de todos los productos generadas');
      await abrirModalEtiquetas();
    } catch (error: any) {
      toast.error('Error al generar las etiquetas: ' + error.message);
    } finally {
      setImprimiendoEtiquetas(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header con título y botones */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{esVistaHerramientas ? 'Herramientas del Taller' : 'Gestión de Stock'}</h1>
        <div className="flex gap-2">
          <button
            onClick={abrirModalExportar}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            <span>{esVistaHerramientas ? 'Exportar Herramientas' : 'Exportar Stock'}</span>
          </button>
          <button
            onClick={() => navigate('/stock/importar')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700"
          >
            <Upload className="w-4 h-4" />
            <span>Importar Excel</span>
          </button>
          <button
            onClick={() => setMostrarFormulario(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-red-600"
          >
            <Plus className="w-4 h-4" />
            <span>{esVistaHerramientas ? 'Agregar Herramienta' : 'Agregar Producto'}</span>
          </button>
          <button
            onClick={() => { const next = !vistaEliminados; setVistaEliminados(next); if (next) cargarProductosEliminados(); }}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${vistaEliminados ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <Trash2 className="w-4 h-4" />
            <span>Eliminados{productosEliminados.length > 0 ? ` (${productosEliminados.length})` : ''}</span>
          </button>
          {!esVistaHerramientas && (
            <button
              onClick={abrirStockBajo}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
            >
              <AlertTriangle className="w-4 h-4" />
              <span>Alerta Stock Bajo</span>
            </button>
          )}
          <button
            onClick={abrirModalEtiquetas}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
          >
            <Barcode className="w-4 h-4" />
            <span>Generar Etiquetas de Código de Barras</span>
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4 mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por nombre o código..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <div className="relative">
          <ScanLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Escanear código de barras (busca o crea el producto)..."
            value={codigoEscaneado}
            onChange={e => setCodigoEscaneado(e.target.value)}
            onKeyDown={manejarEscaneo}
            className="w-full pl-10 pr-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500"
          />
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium w-fit ${esVistaHerramientas ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
          {esVistaHerramientas ? <><Wrench className="w-4 h-4" /> Viendo solo Herramientas del Taller</> : <><Package className="w-4 h-4" /> Viendo solo Stock para Venta</>}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {vistaEliminados
              ? `${esVistaHerramientas ? 'Herramientas' : 'Productos'} Eliminados (${productosEliminados.length})`
              : `Lista de ${esVistaHerramientas ? 'Herramientas' : 'Productos'} (${productosFiltrados.length})`}
          </h2>
        </div>
        {vistaEliminados ? (
          productosEliminados.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Trash2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No hay {esVistaHerramientas ? 'herramientas eliminadas' : 'productos eliminados'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {productosEliminados.map(producto => (
                <div key={producto.id} className="p-6 hover:bg-red-50 border-l-4 border-red-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-lg text-gray-500 line-through">{producto.nombre}</h3>
                        {producto.codigo && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">{producto.codigo}</span>
                        )}
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Eliminado</span>
                      </div>
                      {producto.descripcion && (
                        <p className="text-sm text-gray-400 mb-2">{producto.descripcion}</p>
                      )}
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Precio Venta:</span>
                          <p className="font-semibold text-gray-500">S/ {(producto.precio_venta || 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Stock:</span>
                          <p className="font-semibold text-gray-500">{producto.stock || 0}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Categoria:</span>
                          <p className="font-semibold text-gray-500">{producto.categoria || '-'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => restaurarProducto(producto.id, producto.nombre)}
                        className="text-green-600 hover:text-green-800 px-3 py-2 text-sm flex items-center border border-green-200 rounded-lg hover:bg-green-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restaurar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : productosFiltrados.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No hay {esVistaHerramientas ? 'herramientas registradas' : 'productos registrados'}</p>
          </div>
        ) : (
          <div className="divide-y">
            {productosFiltrados.map(producto => {
              const esEditando = editandoProducto?.id === producto.id;
              const stockBajo = !producto.es_kit && (producto.stock || 0) <= (producto.stock_minimo || 0);

              return (
                <div key={producto.id} className="p-6 hover:bg-gray-50">
                  {esEditando ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                          <input
                            type="text"
                            value={editandoProducto.codigo || ''}
                            onChange={e => setEditandoProducto({ ...editandoProducto, codigo: e.target.value })}
                            className={`w-full p-2 border rounded ${buscarDuenoDelCodigo(editandoProducto.codigo, editandoProducto.id) ? 'border-red-500' : ''}`}
                            placeholder="Código del producto"
                          />
                          {buscarDuenoDelCodigo(editandoProducto.codigo, editandoProducto.id) && (
                            <p className="text-xs text-red-600 mt-1">
                              ⚠️ Ese código ya lo tiene "{buscarDuenoDelCodigo(editandoProducto.codigo, editandoProducto.id)?.nombre}"
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del producto *</label>
                          <input
                            type="text"
                            value={editandoProducto.nombre}
                            onChange={e => setEditandoProducto({ ...editandoProducto, nombre: e.target.value })}
                            className="w-full p-2 border rounded"
                            placeholder="Nombre del producto"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                        <textarea
                          value={editandoProducto.descripcion || ''}
                          onChange={e => setEditandoProducto({ ...editandoProducto, descripcion: e.target.value })}
                          className="w-full p-2 border rounded"
                          rows={2}
                          placeholder="Descripción del producto"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                          <input
                            type="text"
                            value={editandoProducto.categoria || ''}
                            onChange={e => setEditandoProducto({ ...editandoProducto, categoria: e.target.value })}
                            className="w-full p-2 border rounded"
                            placeholder="Categoría"
                          />
                        </div>
                        {editandoProducto.tipo !== 'herramienta' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Precio de venta (S/) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editandoProducto.precio_venta || 0}
                              onChange={e => setEditandoProducto({ ...editandoProducto, precio_venta: parseFloat(e.target.value) || 0 })}
                              className="w-full p-2 border rounded"
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {editandoProducto.tipo === 'herramienta' ? 'Valor de compra (S/)' : 'Precio de compra (S/) *'}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={editandoProducto.precio_compra || 0}
                            onChange={e => setEditandoProducto({ ...editandoProducto, precio_compra: parseFloat(e.target.value) || 0 })}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                        {kitEdit?.es_kit && editandoProducto.tipo !== 'herramienta' ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                            <p className="p-2 border rounded bg-purple-50 text-sm text-purple-800">Es un kit: el stock se calcula con sus piezas (abajo).</p>
                          </div>
                        ) : (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              {editandoProducto.tipo === 'herramienta' ? 'Cantidad en el taller *' : 'Stock actual *'} {editandoProducto.unidad === 'metro' && editandoStockEnCm ? '(en cm)' : ''}
                            </label>
                            {editandoProducto.unidad === 'metro' && (
                              <button
                                type="button"
                                onClick={() => setEditandoStockEnCm(v => !v)}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Ingresar en {editandoStockEnCm ? 'metros' : 'cm'}
                              </button>
                            )}
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            value={(editandoProducto.stock || 0) * (editandoProducto.unidad === 'metro' && editandoStockEnCm ? 100 : 1)}
                            onChange={e => {
                              const factor = editandoProducto.unidad === 'metro' && editandoStockEnCm ? 100 : 1;
                              setEditandoProducto({ ...editandoProducto, stock: (parseFloat(e.target.value) || 0) / factor });
                            }}
                            className="w-full p-2 border rounded"
                            min="0"
                          />
                        </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {editandoProducto.tipo !== 'herramienta' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Stock mínimo</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editandoProducto.stock_minimo || 0}
                              onChange={e => setEditandoProducto({ ...editandoProducto, stock_minimo: parseFloat(e.target.value) || 0 })}
                              className="w-full p-2 border rounded"
                              min="0"
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
                          <select
                            value={editandoProducto.unidad || 'unidad'}
                            onChange={e => { setEditandoProducto({ ...editandoProducto, unidad: e.target.value }); setEditandoStockEnCm(false); }}
                            className="w-full p-2 border rounded"
                          >
                            {UNIDADES_DISPONIBLES.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                          <select
                            value={editandoProducto.tipo || 'venta'}
                            onChange={e => setEditandoProducto({ ...editandoProducto, tipo: e.target.value })}
                            className="w-full p-2 border rounded"
                          >
                            <option value="venta">Para Venta</option>
                            <option value="herramienta">Herramienta</option>
                          </select>
                        </div>
                        {editandoProducto.tipo === 'herramienta' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                            <select
                              value={editandoProducto.condicion || 'en_uso'}
                              onChange={e => setEditandoProducto({ ...editandoProducto, condicion: e.target.value })}
                              className="w-full p-2 border rounded"
                            >
                              <option value="en_uso">En uso</option>
                              <option value="danada">Dañada</option>
                            </select>
                          </div>
                        )}
                      </div>
                      {editandoProducto.tipo !== 'herramienta' && kitEdit && (
                        <KitEditor producto={producto} productos={productos} kit={kitEdit} onChange={setKitEdit}
                                   onIngresado={cargarProductos} precioActual={editandoProducto.precio_venta}
                                   onUsarPrecio={(precio) => setEditandoProducto({ ...editandoProducto, precio_venta: precio })} />
                      )}
                      <div className="flex items-center gap-2">
                        <button onClick={guardarEdicion} className="bg-green-600 text-white px-4 py-2 rounded flex items-center">
                          <Save className="w-4 h-4 mr-1" />Guardar Cambios
                        </button>
                        <button onClick={cancelarEdicion} className="bg-gray-600 text-white px-4 py-2 rounded flex items-center">
                          <X className="w-4 h-4 mr-1" />Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">{producto.nombre}</h3>
                          {producto.es_kit && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-semibold">KIT</span>
                          )}
                          {producto.codigo && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              {producto.codigo}
                            </span>
                          )}
                          {!esVistaHerramientas && stockBajo && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                              Stock Bajo
                            </span>
                          )}
                          {!esVistaHerramientas && (
                            <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${(producto.tipo || 'venta') === 'herramienta' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                              {(producto.tipo || 'venta') === 'herramienta' ? <><Wrench className="w-3 h-3" />Herramienta</> : 'Para Venta'}
                            </span>
                          )}
                          {producto.tipo === 'herramienta' && (
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                              producto.condicion === 'danada' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {producto.condicion === 'danada' ? 'Dañada' : 'En uso'}
                            </span>
                          )}
                        </div>
                        
                        {producto.descripcion && (
                          <p className="text-sm text-gray-600 mb-3">{producto.descripcion}</p>
                        )}
                        {producto.es_kit && (
                          <p className="text-sm text-purple-800 mb-3">
                            Piezas: {(producto.kit_componentes || []).map((c: any) => `${Number(c.cantidad)} × ${c.nombre.trim()} (hay ${Number(c.stock)})`).join(' · ') || 'sin piezas definidas'}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          {esVistaHerramientas ? (
                            <>
                              <div>
                                <span className="text-gray-500">Cantidad en el taller:</span>
                                <p className="font-semibold text-gray-900">
                                  {producto.stock || 0} {producto.unidad || 'unidad'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Valor de compra:</span>
                                <p className="font-semibold text-gray-700">
                                  S/ {(producto.precio_compra || 0).toFixed(2)}
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span className="text-gray-500">Precio Venta:</span>
                                <p className="font-semibold text-green-600">
                                  S/ {(producto.precio_venta || 0).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">{producto.es_kit ? 'Kits armables:' : 'Stock Actual:'}</span>
                                <p className={`font-semibold ${stockBajo ? 'text-red-600' : 'text-gray-900'}`}>
                                  {producto.stock || 0} {producto.es_kit ? 'kit(s)' : (producto.unidad || 'unidad')}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Stock Mínimo:</span>
                                <p className="font-semibold text-orange-600">
                                  {producto.stock_minimo || 0} {producto.unidad || 'unidad'}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {producto.categoria && (
                          <div className="mt-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {producto.categoria}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-4 flex gap-2">
                        <button 
                          onClick={() => mostrarDetallesProducto(producto)} 
                          className="text-indigo-600 hover:text-indigo-800 px-3 py-2 text-sm flex items-center border border-indigo-200 rounded-lg hover:bg-indigo-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Detalles
                        </button>
                        <button 
                          onClick={() => iniciarEdicion(producto)} 
                          className="text-red-500 hover:text-blue-800 px-3 py-2 text-sm flex items-center border border-blue-200 rounded-lg hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </button>
                        <button
                          onClick={() => eliminarProducto(producto.id, producto.nombre)}
                          className="text-red-600 hover:text-red-800 px-3 py-2 text-sm flex items-center border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Agregar Producto */}
      {mostrarFormulario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Plus className="mr-2 h-5 w-5" />
                {esVistaHerramientas ? 'Agregar Herramienta' : 'Agregar Producto'}
              </h2>
              <button
                onClick={() => setMostrarFormulario(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); agregarProducto(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Código</label>
                <input
                  type="text"
                  value={nuevoProducto.codigo}
                  onChange={(e) => setNuevoProducto({...nuevoProducto, codigo: e.target.value})}
                  placeholder="Deja vacío para generar automáticamente"
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${buscarDuenoDelCodigo(nuevoProducto.codigo) ? 'border-red-500' : ''}`}
                />
                {buscarDuenoDelCodigo(nuevoProducto.codigo) && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ Ese código ya lo tiene "{buscarDuenoDelCodigo(nuevoProducto.codigo)?.nombre}"
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <input
                  type="text"
                  value={nuevoProducto.nombre}
                  onChange={(e) => setNuevoProducto({...nuevoProducto, nombre: e.target.value})}
                  required
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea
                  value={nuevoProducto.descripcion}
                  onChange={(e) => setNuevoProducto({...nuevoProducto, descripcion: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {!esVistaHerramientas && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio Venta</label>
                    <input
                      type="number"
                      step="0.01"
                      value={nuevoProducto.precio_venta}
                      onChange={(e) => setNuevoProducto({...nuevoProducto, precio_venta: parseFloat(e.target.value) || 0})}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">{esVistaHerramientas ? 'Valor de compra' : 'Precio Compra'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={nuevoProducto.precio_compra}
                    onChange={(e) => setNuevoProducto({...nuevoProducto, precio_compra: parseFloat(e.target.value) || 0})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría</label>
                  <input
                    type="text"
                    value={nuevoProducto.categoria}
                    onChange={(e) => setNuevoProducto({...nuevoProducto, categoria: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Se va a guardar como <strong>{esVistaHerramientas ? 'Herramienta' : 'Para Venta'}</strong> (según la pantalla en la que estás). Para cambiarlo despues, editá el producto.
              </p>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">
                      {esVistaHerramientas ? 'Cantidad' : 'Stock Inicial'} {nuevoProducto.unidad === 'metro' && nuevoStockEnCm ? '(en cm)' : ''}
                    </label>
                    {nuevoProducto.unidad === 'metro' && (
                      <button
                        type="button"
                        onClick={() => setNuevoStockEnCm(v => !v)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Ingresar en {nuevoStockEnCm ? 'metros' : 'cm'}
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={nuevoProducto.stock * (nuevoProducto.unidad === 'metro' && nuevoStockEnCm ? 100 : 1)}
                    onChange={(e) => {
                      const factor = nuevoProducto.unidad === 'metro' && nuevoStockEnCm ? 100 : 1;
                      setNuevoProducto({...nuevoProducto, stock: (parseFloat(e.target.value) || 0) / factor});
                    }}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {!esVistaHerramientas && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Stock Mínimo</label>
                    <input
                      type="number"
                      step="0.01"
                      value={nuevoProducto.stock_minimo}
                      onChange={(e) => setNuevoProducto({...nuevoProducto, stock_minimo: parseFloat(e.target.value) || 0})}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unidad</label>
                <select
                  value={nuevoProducto.unidad}
                  onChange={(e) => { setNuevoProducto({...nuevoProducto, unidad: e.target.value}); setNuevoStockEnCm(false); }}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {UNIDADES_DISPONIBLES.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setMostrarFormulario(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-red-500 flex items-center justify-center"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalles del Producto */}
      {mostrarDetalles && productoDetalles && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Eye className="mr-2 h-5 w-5 text-indigo-600" />
                Detalles del Producto
              </h2>
              <button
                onClick={cerrarDetalles}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900">{productoDetalles.nombre}</h3>
                {productoDetalles.codigo && (
                  <p className="text-sm text-gray-600">Código: {productoDetalles.codigo}</p>
                )}
                {productoDetalles.descripcion && (
                  <p className="text-sm text-gray-600 mt-1">{productoDetalles.descripcion}</p>
                )}
              </div>

              {productoDetalles.tipo === 'herramienta' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-700 font-medium">Cantidad en el taller</span>
                      <p className="text-lg font-bold text-gray-900">
                        {productoDetalles.stock || 0} {productoDetalles.unidad || 'unidad'}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-sm text-blue-700 font-medium">Valor de compra</span>
                      <p className="text-lg font-bold text-blue-800">
                        S/ {(productoDetalles.precio_compra || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${productoDetalles.condicion === 'danada' ? 'bg-red-50' : 'bg-green-50'}`}>
                    <span className={`text-sm font-medium ${productoDetalles.condicion === 'danada' ? 'text-red-700' : 'text-green-700'}`}>Estado</span>
                    <p className={`text-lg font-bold ${productoDetalles.condicion === 'danada' ? 'text-red-800' : 'text-green-800'}`}>
                      {productoDetalles.condicion === 'danada' ? 'Dañada' : 'En uso'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <span className="text-sm text-green-700 font-medium">Precio de Venta</span>
                      <p className="text-lg font-bold text-green-800">
                        S/ {(productoDetalles.precio_venta || 0).toFixed(2)}
                      </p>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-sm text-blue-700 font-medium">Precio de Compra</span>
                      <p className="text-lg font-bold text-blue-800">
                        S/ {(productoDetalles.precio_compra || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-700 font-medium">Stock Actual</span>
                      <p className="text-lg font-bold text-gray-900">
                        {productoDetalles.stock || 0} {productoDetalles.unidad || 'unidad'}
                      </p>
                    </div>

                    <div className="bg-orange-50 p-3 rounded-lg">
                      <span className="text-sm text-orange-700 font-medium">Stock Mínimo</span>
                      <p className="text-lg font-bold text-orange-800">
                        {productoDetalles.stock_minimo || 0} {productoDetalles.unidad || 'unidad'}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {productoDetalles.categoria && (
                <div className="bg-purple-50 p-3 rounded-lg">
                  <span className="text-sm text-purple-700 font-medium">Categoría</span>
                  <p className="text-lg font-bold text-purple-800">{productoDetalles.categoria}</p>
                </div>
              )}

              {/* Cálculo de Margen de Ganancia */}
              {productoDetalles.precio_venta && productoDetalles.precio_compra && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <span className="text-sm text-yellow-700 font-medium">Margen de Ganancia</span>
                  <p className="text-lg font-bold text-yellow-800">
                    S/ {(productoDetalles.precio_venta - productoDetalles.precio_compra).toFixed(2)} 
                    ({(((productoDetalles.precio_venta - productoDetalles.precio_compra) / productoDetalles.precio_compra) * 100).toFixed(1)}%)
                  </p>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={cerrarDetalles}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Exportar Stock ─────────────────────────────────────────── */}
      {mostrarModalExportar && (() => {
        const base = [...productos]
          .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));

        const listaMostrar = base.filter((p: any) => {
          if (filtroExportar === 'con_stock') return (p.stock || 0) > 0;
          if (filtroExportar === 'sin_stock') return (p.stock || 0) === 0;
          return true;
        });

        const todosSeleccionados = listaMostrar.length > 0 && listaMostrar.every((p: any) => seleccionados.has(p.id));
        const algunoSeleccionado = listaMostrar.some((p: any) => seleccionados.has(p.id));

        const toggleTodos = () => {
          setSeleccionados(prev => {
            const next = new Set(prev);
            if (todosSeleccionados) { listaMostrar.forEach((p: any) => next.delete(p.id)); }
            else { listaMostrar.forEach((p: any) => next.add(p.id)); }
            return next;
          });
        };

        const toggleUno = (id: string) => {
          setSeleccionados(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
          });
        };

        const countSel = listaMostrar.filter((p: any) => seleccionados.has(p.id)).length;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{maxHeight: '90vh'}}>

              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b bg-blue-600 rounded-t-2xl">
                <div className="flex items-center gap-2 text-white">
                  <Download className="w-5 h-5" />
                  <div>
                    <h2 className="text-lg font-bold">Exportar Stock</h2>
                    <p className="text-blue-100 text-xs">Selecciona los productos a incluir en el reporte</p>
                  </div>
                </div>
                <button onClick={() => setMostrarModalExportar(false)} className="text-white hover:bg-blue-700 p-1 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Filtros */}
              <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600 font-medium">Mostrar:</span>
                {([['todos','Todos'],['con_stock','Con Stock'],['sin_stock','Sin Stock']] as const).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setFiltroExportar(val)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filtroExportar === val
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Seleccionar todos */}
              <div className="px-5 py-2 border-b flex items-center justify-between bg-blue-50">
                <button onClick={toggleTodos} className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900">
                  {todosSeleccionados
                    ? <CheckSquare className="w-4 h-4" />
                    : algunoSeleccionado
                      ? <CheckSquare className="w-4 h-4 opacity-50" />
                      : <Square className="w-4 h-4" />
                  }
                  {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
                <span className="text-xs text-gray-500">{countSel} de {listaMostrar.length} seleccionados</span>
              </div>

              {/* Lista de productos */}
              <div className="overflow-y-auto flex-1 divide-y">
                {listaMostrar.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No hay productos en esta categoría</p>
                  </div>
                ) : listaMostrar.map((p: any) => {
                  const stockActual = p.stock || 0;
                  const stockMinimo = p.stock_minimo || 0;
                  const isSel = seleccionados.has(p.id);
                  const estado = stockActual === 0 ? 'SIN STOCK' : stockActual <= stockMinimo ? 'STOCK BAJO' : 'NORMAL';
                  const estadoColor = stockActual === 0 ? 'bg-red-100 text-red-700' : stockActual <= stockMinimo ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700';

                  return (
                    <div
                      key={p.id}
                      onClick={() => toggleUno(p.id)}
                      className={`flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${isSel ? 'bg-blue-50' : ''}`}
                    >
                      {isSel
                        ? <CheckSquare className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        : <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      }
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{p.nombre}</p>
                        <p className="text-xs text-gray-400">{p.codigo || 'Sin código'} {p.categoria ? '· ' + p.categoria : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-700">S/ {(p.precio_venta || 0).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${estadoColor}`}>
                          {estado === 'NORMAL' ? `${stockActual} uds` : estado}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex items-center justify-between gap-3">
                <span className="text-sm text-gray-500">
                  {countSel === 0 ? 'Ningún producto seleccionado' : `${countSel} producto${countSel > 1 ? 's' : ''} listo${countSel > 1 ? 's' : ''} para exportar`}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMostrarModalExportar(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={descargarSeleccionados}
                    disabled={countSel === 0}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
                      countSel === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    Descargar Excel
                  </button>
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {mostrarStockBajo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Productos con Stock Bajo
              </h2>
              <button onClick={() => setMostrarStockBajo(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {cargandoStockBajo ? (
                <p className="text-center text-gray-500 py-8">Cargando...</p>
              ) : stockBajoLista.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay productos con stock bajo por ahora.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">Código</th>
                      <th className="px-2 py-2 text-left">Nombre</th>
                      <th className="px-2 py-2 text-right">Stock</th>
                      <th className="px-2 py-2 text-right">Mínimo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {stockBajoLista.map((p) => (
                      <tr key={p.id} className={Number(p.stock) === 0 ? 'bg-red-50' : 'bg-yellow-50'}>
                        <td className="px-2 py-2">{p.codigo}</td>
                        <td className="px-2 py-2">{p.nombre}</td>
                        <td className="px-2 py-2 text-right font-semibold">{p.stock}</td>
                        <td className="px-2 py-2 text-right text-gray-500">{p.stock_minimo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setMostrarStockBajo(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
              <button
                onClick={descargarPdfStockBajo}
                disabled={descargando || stockBajoLista.length === 0}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {descargando ? 'Generando...' : 'Descargar PDF (hoja de control)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarEtiquetas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Barcode className="w-5 h-5 text-gray-700" />
                Generar Etiquetas de Código de Barras
              </h2>
              <button onClick={() => setMostrarEtiquetas(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 pt-3 text-sm text-gray-600 flex items-center justify-between">
              <p>Se preseleccionan automáticamente los productos <strong>recién agregados</strong> y <strong>editados</strong> desde su última impresión. Ajustá la selección o usá "Imprimir Todos" para forzar todo.</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {cargandoEtiquetas ? (
                <p className="text-center text-gray-500 py-8">Cargando...</p>
              ) : etiquetasLista.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay productos con código interno para etiquetar.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left w-8">
                        <input
                          type="checkbox"
                          checked={etiquetasSeleccionadas.size === etiquetasLista.length}
                          onChange={(e) => setEtiquetasSeleccionadas(e.target.checked ? new Set(etiquetasLista.map((p: any) => p.id)) : new Set())}
                        />
                      </th>
                      <th className="px-2 py-2 text-left">Código</th>
                      <th className="px-2 py-2 text-left">Nombre</th>
                      <th className="px-2 py-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {etiquetasLista.map((p: any) => (
                      <tr key={p.id} className={etiquetasSeleccionadas.has(p.id) ? 'bg-blue-50' : ''}>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={etiquetasSeleccionadas.has(p.id)}
                            onChange={() => alternarSeleccionEtiqueta(p.id)}
                          />
                        </td>
                        <td className="px-2 py-2">{p.codigo}</td>
                        <td className="px-2 py-2">{p.nombre}</td>
                        <td className="px-2 py-2 text-center">
                          {p.estado_etiqueta === 'nuevo' && (
                            <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Nuevo</span>
                          )}
                          {p.estado_etiqueta === 'editado' && (
                            <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">Editado</span>
                          )}
                          {p.estado_etiqueta === 'al_dia' && (
                            <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Al día</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t flex justify-between items-center gap-2">
              <p className="text-sm text-gray-500">{etiquetasSeleccionadas.size} seleccionado(s) de {etiquetasLista.length}</p>
              <div className="flex gap-2">
                <button
                  onClick={imprimirEtiquetasSeleccionadas}
                  disabled={imprimiendoEtiquetas || etiquetasSeleccionadas.size === 0}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {imprimiendoEtiquetas ? 'Generando...' : `Imprimir Seleccionados (${etiquetasSeleccionadas.size})`}
                </button>
                <button
                  onClick={imprimirTodasLasEtiquetas}
                  disabled={imprimiendoEtiquetas || etiquetasLista.length === 0}
                  className="bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-800 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  Imprimir Todos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockCompleto;
